import axios from 'axios';
import './account.css';
import { extend, getCookie } from './utils';
import { modalTpl, pageTpl, imageCaptchaTpl } from './AccountTpl';
import { addClass, removeClass, hasClass, toggleClass } from './class';

/* eslint vars-on-top: off */
// 手机+验证码登录
const MOBILE_CODE_LOGIN_URL = '/passport/web/sms_login/?account_sdk_source=web';
// 手机/邮箱 + 密码登录
const ACCOUNT_PWD_LOGIN_URL = '/passport/web/user/login/?account_sdk_source=web';
// 密码设置
const PWD_SET_URL = '/passport/web/password/set/?account_sdk_source=web';
// 密码修改
const PWD_CHANGE_URL = '/passport/web/password/change/?account_sdk_source=web';
// 密码重置
const PWD_RESET_URL = '/passport/web/password/reset/?account_sdk_source=web';
// 手机验证码
const MOBILE_CODE_URL = '/passport/web/send_code/?account_sdk_source=web';
// 手机绑定
const MOBILE_BIND_URL = '/passport/web/mobile/bind/?account_sdk_source=web';
// 图片验证码
const CAPTCHA_CODE_URL = '/passport/web/refresh_captcha/?account_sdk_source=web';
// 登出
const LOGOUT_URL = '/passport/web/logout/?account_sdk_source=web';
// 图形验证码最大长度
const MAX_IMG_CAPTCHA_LEN = 4;

// 网络请求出错
const NET_ERROR = {
    status: 'fail',
    message: '网络请求失败，请重试',
    error_code: -1
};

// 重复请求接口
const BUSY_ERROR = {
    status: 'fail',
    mesage: '请勿重复请求接口',
    error_code: -2
};

// 结果出错
const rejectError = (data, needCaptchaWhenReject) => extend({
    status: 'fail',
    message: data.description || '',
    error_code: data.error_code
}, needCaptchaWhenReject ? { captcha: data.captcha || '' } : {});

// event enum
const EVENT = {
    loginSuccess: 'loginSuccess', // 登录成功
    loginFailure: 'loginFailure', // 登录失败
    beforeLogin: 'beforeLogin', // 登录之前
    beforeSendPhoneCaptcha: 'beforeSendingPhoneCaptcha', // 发送验证码之前
    sendPhoneCaptchaSuccess: 'sendPhoneCaptchaSuccess', // 发送验证码成功
    sendPhoneCaptchaFailure: 'sendPhoneCaptchaFailure', // 发送验证码失败
    toggleAgreement: 'toggleAgreement', // 同意或者不同意政策
    dismissModal: 'dismissModal' // 模态框消失
};

const noop = () => {};
const trimReg = /^\s*|\s*$/g;

/**
 * 登录类
 * opts { aid }
 * modal: boolean modal or page
 */
/* eslint no-underscore-dangle: off */
/* eslint no-param-reassign: off */
/* eslint prefer-promise-reject-errors: off */
/* eslint class-methods-use-this: off */
class AccountSDK {
    constructor({
        aid,
        modal = false,
        dom = 'body',
        countDownNote = '%nums重新获取',
        countDownInterval = 60,
        countDownInitNote = '获取验证码',
        agreementHtml = '',
        loginStr = '登录',
        loggingStr = '登录中...',
        defaultCheckedAgreement = true,
        loginChecker = e => true,
        checkPhoneRe = /^1\d{10}$/,
        checkPhoneCaptchaRe = /\d{4}/,
        messageTimeout = 2000,
        urlOrigin = location.origin,
        customTitle = '登录你的头条，精彩不丢失',
        formData = true,
        defaultPhone = ''
    } = {}) {
        this._default = {
            aid: aid || 13
        };
        this._modal = modal;
        this._countDownNote = countDownNote;
        this._countDownInterval = countDownInterval;
        this._countDownInitNote = countDownInitNote;
        this._agreementHtml = agreementHtml;
        this._loginStr = loginStr;
        this._loggingStr = loggingStr;
        this._container$ = modal ? null : document.querySelector(dom);
        this._submitBtn$ = null;
        this._phoneInput$ = null;
        this._phoneCaptchaInput$ = null;
        this._imageCaptchaInput$ = null;
        this._phoneCaptchaBtn$ = null;
        this._phoneErrorNote$ = null;
        this._phoneCaptchaErrorNote$ = null;
        this._agreement$ = null;
        this._commonToast$ = null;
        this._captchaToast$ = null;
        this._registerNote$ = null;
        this._gettingPhoneCaptcha = false;
        this._isLogging = false;
        this._phoneNoticeTimer = 0;
        this._phoneCaptchaNoticeTimer = 0;
        this._toastTimer = 0;
        this._codeTimer = 0;
        this._defaultCheckedAgreement = defaultCheckedAgreement;
        this._loginChecker = loginChecker;
        this._checkPhoneRe = checkPhoneRe;
        this._checkPhoneCaptchaRe = checkPhoneCaptchaRe;
        this._messageTimeout = messageTimeout;
        this._toastTimerFunc = noop;
        this._urlOrigin = urlOrigin;
        this._customTitle = customTitle;
        this._stringifyData = formData;
        this._defaultPhone = defaultPhone;
        // whether or not user need to input image captcha to login
        this._needImageCaptcha = false;
        this._formContainer$ = null;
        // img element with src set to captcha
        // used to replace captcha when user click image
        this._captchaImg$ = null;
        // container of image captcha
        this._imageCaptchaContainer$ = null;
        this._initDOM();
        this._initListener();
    }

    _emit(eventName, detail = {}) {
        const e = new CustomEvent(eventName, {
            detail,
            bubbles: true,
            cancelable: false
        });
        this._container$.dispatchEvent(e);
    }

    _urlWrap(url) {
        return this._urlOrigin + url;
    }

    _formData(params) {
        if (typeof params !== 'object' || !this._stringifyData) return params;
        let query = '';
        for (let key in params) {
            if (!params.hasOwnProperty(key)) continue;
            if (query) query += '&';
            query += `${key}=${encodeURIComponent(params[key])}`;
        }
        return query;
    }

    // 发送手机号验证码
    _captchaToast() {
        removeClass(this._captchaToast$, 'hidden');
        this._toastTimerFunc = () => {
            this._toastTimer = 0;
            addClass(this._captchaToast$, 'hidden');
            this._toastTimerFunc = noop;
        };
        this._toastTimer = setTimeout(this._toastTimerFunc, this._messageTimeout);
    }

    // 发送toast提示
    _toast(message) {
        this._commonToast$.innerText = message;
        removeClass(this._commonToast$, 'hidden');
        this._toastTimerFunc = () => {
            this._toastTimer = 0;
            this._commonToast$.innerText = '';
            addClass(this._commonToast$, 'hidden');
            this._toastTimerFunc = noop;
        };
        this._toastTimer = setTimeout(this._toastTimerFunc, this._messageTimeout);
    }

    // 展示页面表单的错误提示
    _phoneNotice(message) {
        clearTimeout(this._phoneNoticeTimer);
        removeClass(this._phoneErrorNote$, 'hidden');
        this._phoneErrorNote$.innerText = message;
        this._phoneNoticeTimer = setTimeout(() => {
            addClass(this._phoneErrorNote$, 'hidden');
            this._phoneNoticeTimer = 0;
        }, this._messageTimeout);
    }

    _phoneCaptchaNotice(message) {
        clearTimeout(this._phoneCaptchaNoticeTimer);
        removeClass(this._phoneCaptchaErrorNote$, 'hidden');
        this._modal && addClass(this._registerNote$, 'hidden');
        this._phoneCaptchaErrorNote$.innerText = message;
        this._phoneCaptchaNoticeTimer = setTimeout(() => {
            addClass(this._phoneCaptchaErrorNote$, 'hidden');
            this._modal && removeClass(this._registerNote$, 'hidden');
            this._phoneCaptchaNoticeTimer = 0;
        }, this._messageTimeout);
    }

    // 参数打包
    _paramsPack(params) {
        if (typeof params !== 'object') {
            params = {};
        }
        params = params || {};
        return extend(params, this._default);
    }

    _getPhone() {
        return this._phoneInput$.value.replace(trimReg, '');
    }

    _getPhoneCaptcha() {
        return this._phoneCaptchaInput$.value.replace(trimReg, '');
    }

    _getGraphicCaptcha() {
        return this._imageCaptchaInput$ && this._imageCaptchaInput$.value.replace(trimReg, '');
    }

    _getAgreementStatus() {
        return this._agreementHtml ? hasClass(this._agreement$, 'checked') : true;
    }

    done() {
        this._container$.removeEventListener('click', this);
        clearTimeout(this._codeTimer);
        clearTimeout(this._toastTimer);
        clearTimeout(this._phoneNoticeTimer);
        clearTimeout(this._phoneCaptchaNoticeTimer);
        if (this._modal) this._container$.parentNode.removeChild(this._container$);
        this._container$ = null;
        this._submitBtn$ = null;
        this._phoneInput$ = null;
        this._phoneCaptchaInput$ = null;
        this._phoneCaptchaBtn$ = null;
        this._phoneErrorNote$ = null;
        this._phoneCaptchaErrorNote$ = null;
        this._agreement$ = null;
        this._commonToast$ = null;
        this._captchaToast$ = null;
        this._registerNote$ = null;
        this._phoneCaptchaContainer$ = null;
        this._formContainer$ = null;
    }

    // 渲染dom
    _initDOM() {
        if (this._modal) {
            const div = document.createElement('div');
            div.innerHTML = modalTpl(this._agreementHtml, this._loginStr, this._defaultCheckedAgreement, this._customTitle);
            document.body.appendChild(div.children[0]);
            this._container$ = document.body.querySelector('.login-form-modal');
        } else {
            this._container$.innerHTML = pageTpl(this._agreementHtml, this._loginStr, this._defaultCheckedAgreement, this._customTitle);
        }
        this._submitBtn$ = this._container$.querySelector('.login-form-submit-btn');
        this._phoneInput$ = this._container$.querySelector('input[name=phone]');
        this._phoneCaptchaInput$ = this._container$.querySelector('input[name=phonecaptcha]');
        this._phoneCaptchaContainer$ = document.getElementById('login-form-phone-captcha');
        this._phoneCaptchaBtn$ = this._container$.querySelector('.login-form-phonecaptcha-btn');
        this._phoneErrorNote$ = this._container$.querySelector('.login-form-input-row.phone .error-msg');
        this._phoneCaptchaErrorNote$ = this._container$.querySelector('.login-form-input-row.captcha .error-msg');
        this._commonToast$ = this._container$.querySelector('.login-toast');
        this._captchaToast$ = this._container$.querySelector('.captcha-toast');
        this._registerNote$ = this._container$.querySelector('.login-form-notice');
        this._formContainer$ = this._container$.querySelector('.login-form-wrapper');
        this._agreementHtml && (this._agreement$ = this._container$.querySelector('.login-form-agreement'));

        // put default value
        this._defaultPhone && (this._phoneInput$.value = this._defaultPhone);
    }

    /**
     * prefix data image string with type
     * @param {string} dataImgContent - raw data image string
     * @return {string} full data image string
     */
    _prefixDataImg(dataImgContent) {
        return `data:image/png;base64, ${dataImgContent}`;
    }

    /**
     * set captcha image source
     * @param {string} captcha - image captcha data image
     * @return {void} None
     */
    _setCaptchaImage(captcha) {
        captcha && this._captchaImg$ && (this._captchaImg$.src = this._prefixDataImg(captcha));
    }

    /**
     * @param {string} captcha image data uri
     * create captcha DOM Component and append it to $container
     * @returns {void} None
     */
    _attachCaptchaDom(captcha) {
        this._imageCaptchaContainer$ = document.createElement('div');
        this._imageCaptchaContainer$.innerHTML = imageCaptchaTpl(captcha);
        this._formContainer$.insertBefore(this._imageCaptchaContainer$, this._phoneCaptchaContainer$);
        // this._formContainer$.appendChild(this._imageCaptchaContainer$);
        this._captchaImg$ = this._formContainer$.querySelector('.login-form-img-captcha-pic__img');
        this._imageCaptchaInput$ = this._formContainer$.querySelector('.login-form-img-captcha__input');
        this._needImageCaptcha = true;
    }

    // event handler
    handleEvent(e) {
        if (this._isLogging) return;
        if (this._toastTimer) {
            clearTimeout(this._toastTimer);
            this._toastTimerFunc();
            return;
        }
        const target = e.target;
        const className = target.className;
        switch (e.type) {
            case 'click':
                if (target === this._submitBtn$) {
                    this.userLogin();
                } else if (target === this._phoneCaptchaBtn$) {
                    this.getMobileCode();
                } else if (target === this._agreement$ || target === this._agreement$.querySelector('i') || target === this._agreement$.querySelector('div')) {
                    this._toggleAgreement(e);
                } else if (className === 'login-form-close-btn') {
                    this._emit(EVENT.dismissModal);
                } else if (
                    className === 'login-form-img-captcha-pic__img-wrapper'
                    || className === 'login-form-img-captcha-pic__img'
                ) {
                    axios.get(this._urlWrap(CAPTCHA_CODE_URL)).then(({ data }) => {
                        const { data: { captcha } = {} } = data;
                        this._setCaptchaImage(captcha);
                    });
                }
                break;
            case 'touchmove':
                if (this._modal) {
                    e.preventDefault();
                }
                break;
            case 'input':
                if (target === this._imageCaptchaInput$) {
                    if (this._needImageCaptcha) {
                        const { value } = this._imageCaptchaInput$;
                        if (value.length > MAX_IMG_CAPTCHA_LEN) {
                            this._imageCaptchaInput$.value = value.slice(0, MAX_IMG_CAPTCHA_LEN);
                        }
                        this._imageCaptchaInput$.value = this._imageCaptchaInput$.value.toUpperCase();
                    }
                }
                break;
            default:
                break;
        }
    }

    _toggleAgreement(e) {
        toggleClass(this._agreement$, 'checked');
        this._emit(EVENT.toggleAgreement, { checked: this._getAgreementStatus() });
    }

    // 监听事件
    _initListener() {
        this._container$.addEventListener('click', this, true);
        this._container$.addEventListener('touchmove', this, true);
        this._container$.addEventListener('input', this, true);
    }

    /**
     * 用户登录
     * accountType === false, params: { mobile, code, captcha, mix_mode }
     * accountType === true, params: { account, password, captcha, mix_mode }
     */
    userLogin(accountType = false) {
        if (this._isLogging) return;
        const loginUrl = accountType ? this._urlWrap(ACCOUNT_PWD_LOGIN_URL) : this._urlWrap(MOBILE_CODE_LOGIN_URL);
        let params = {
            mobile: this._getPhone(),
            code: this._getPhoneCaptcha()
        };
        if (!params.mobile) {
            this._phoneNotice('手机号码不为空');
            return;
        }
        if (!this._checkPhoneRe.test(params.mobile)) {
            this._phoneNotice('手机号错误');
            return;
        }
        if (!params.code) {
            this._phoneCaptchaNotice('请输入验证码');
            return;
        }
        if (!this._checkPhoneCaptchaRe.test(params.code)) {
            this._phoneCaptchaNotice('验证码错误');
            return;
        }
        if (!this._getAgreementStatus()) {
            this._toast('尚未同意协议');
            return;
        }
        if (this._needImageCaptcha) {
            Object.defineProperty(params, 'captcha', {
               enumerable: true,
               value: this._getGraphicCaptcha()
            });
        }
        this._paramsPack(params);
        this._emit(EVENT.beforeLogin, params);
        if (!this._loginChecker(params)) return;
        this._isLogging = true;
        this._submitBtn$.innerText = this._loggingStr;
        addClass(this._submitBtn$, 'disable');
        axios.post(loginUrl, this._formData(params), {
            'X-CSRFToken': getCookie('csrftoken')
        }).then(res => {
            this._isLogging = false;
            this._submitBtn$.innerText = this._loginStr;
            removeClass(this._submitBtn$, 'disable');
            res = res.data;
            const data = res.data || {};
            if (res.message === 'success') {
                this._emit(EVENT.loginSuccess, { data, params });
            } else {
                this._emit(EVENT.loginFailure, { data, params });
                this._toast(data.description || '登录失败');
            }
        }).catch(err => {
            this._isLogging = false;
            this._submitBtn$.innerText = this._loginStr;
            removeClass(this._submitBtn$, 'disable');
            this._emit(EVENT.loginFailure, { params, data: {} });
            this._toast('登录失败');
        });
    }

    /**
     * 获取手机验证码
     * params { mobile, captcha, type }
     */
    getMobileCode() {
        if (this._gettingPhoneCaptcha) return;
        let params = {
            mobile: this._getPhone(),
            type: 24
        };
        if (!params.mobile) {
            this._toast('手机号码不为空');
            return;
        }
        if (!this._checkPhoneRe.test(params.mobile)) {
            this._toast('请填写正确的手机号码');
            return;
        }
        if (this._needImageCaptcha) {
            Object.defineProperty(params, 'captcha', {
                enumerable: true,
                value: this._getGraphicCaptcha()
            });
        }
        this._gettingPhoneCaptcha = true;
        this._paramsPack(params);
        this._emit(EVENT.beforeSendPhoneCaptcha, params);
        axios.get(this._urlWrap(MOBILE_CODE_URL), { params }).then(res => {
            res = res.data;
            const data = res.data || {};
            if (res.message === 'success') {
                this.setCodeStatus();
                this._captchaToast();
                this._emit(EVENT.sendPhoneCaptchaSuccess, { mobile: params.mobile });
            } else {
                this._toast(data.description || '发送验证码失败');
                if (data.captcha) {
                    if (!this._needImageCaptcha) {
                        this._attachCaptchaDom(this._prefixDataImg(data.captcha));
                        // caveat: 经测试发现验证码错误后，之前的验证码已经不合法，需要将图片刷新为返回值里的captcha
                    } else {
                        this._setCaptchaImage(data.captcha);
                    }
                } else {
                    this._emit(EVENT.sendPhoneCaptchaFailure, { mobile: params.mobile });
                }
                this._gettingPhoneCaptcha = false;
            }
        }).catch(err => {
            this._gettingPhoneCaptcha = false;
            this._toast('发送验证码失败');
            this._emit(EVENT.sendPhoneCaptchaFailure, { mobile: params.mobile });
            this._gettingPhoneCaptcha = false;
        });
    }

    /**
     * 设置按钮倒计时状态文字
     */
    setCodeStatus() {
        let countDown = this._countDownInterval;
        this._codeTimer = 0;
        addClass(this._phoneCaptchaBtn$, 'disable');
        const ticker = () => {
            if (countDown === 0) {
                this._phoneCaptchaBtn$.innerText = this._countDownInitNote;
                removeClass(this._phoneCaptchaBtn$, 'disable');
                this._gettingPhoneCaptcha = false;
                return;
            }
            this._phoneCaptchaBtn$.innerText = this._countDownNote.replace('%num', countDown);
            countDown -= 1;
            this._codeTimer = setTimeout(ticker, 1000);
        };
        ticker();
    }

    /**
     * 登出
    */
    /* eslint no-restricted-globals: off */
    logout(nextUrl = '/') {
        nextUrl = encodeURIComponent(nextUrl);
        location.href = `${LOGOUT_URL}&next=${nextUrl}`;
    }
}

export default AccountSDK;
